import React from "react";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import { strings } from "../../resources/strings";

interface LaunchDarklyProviderProps {
  children: React.ReactNode;
  clientSideID: string;
}

/**
 * LaunchDarkly Provider Component
 *
 * This component initializes the LaunchDarkly SDK and provides feature flag
 * context to the entire application.
 *
 * @param children - The child components to wrap with LaunchDarkly context
 * @param clientSideID - The LaunchDarkly client-side ID for this environment
 */
const LaunchDarklyProviderComponent: React.FC<LaunchDarklyProviderProps> = ({
  children,
  clientSideID,
}) => {
  const [LDProvider, setLDProvider] = React.useState<React.ComponentType<{
    children: React.ReactNode;
  }> | null>(null);

  React.useEffect(() => {
    // Initialize LaunchDarkly asynchronously
    const initializeLaunchDarkly = async () => {
      try {
        const provider = await asyncWithLDProvider({
          clientSideID,
          context: {
            kind: "user",
            key: "anonymous", // Will be updated with actual user after authentication
            anonymous: true,
          },
          options: {
            // Send events to LaunchDarkly
            sendEvents: true,
            // Enable streaming for real-time flag updates
            streaming: true,
            // Cache flags in localStorage for faster subsequent loads
            bootstrap: "localStorage",
          },
        });
        setLDProvider(() => provider);
      } catch (error) {
        console.error("Failed to initialize LaunchDarkly:", error);
        // If LaunchDarkly fails to initialize, render children without provider
        // This ensures the app still works even if LaunchDarkly is unavailable
        setLDProvider(() => ({ children }: { children: React.ReactNode }) => (
          <>{children}</>
        ));
      }
    };

    if (clientSideID) {
      initializeLaunchDarkly();
    } else {
      console.warn(
        "LaunchDarkly client ID not provided. Feature flags will use default values.",
      );
      // Render without provider if no client ID
      setLDProvider(() => ({ children }: { children: React.ReactNode }) => (
        <>{children}</>
      ));
    }
  }, [clientSideID]);

  // Show loading state while LaunchDarkly initializes
  if (!LDProvider) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <p>{strings.loading}</p>
      </div>
    );
  }

  return <LDProvider>{children}</LDProvider>;
};

export default LaunchDarklyProviderComponent;
